import { cn } from '@/lib/utils';
import { useRef, useState } from 'react';
import { WithContext as ReactTags, type Tag } from 'react-tag-input';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

export const TagInput = ({
  initialValue = [],
  value: controlledValue,
  onValueChange,
  onInvalidValue,
  placeholder,
  maxTags,
  className,
  tagClassName,
  tagInputFieldClassName,
  removeClassName,
  tagRegex = /.*/,
}: {
  initialValue?: Tag[];
  value?: Tag[];
  onValueChange?: (tags: Tag[]) => void;
  onInvalidValue?: (value: string) => void;
  placeholder?: string;
  maxTags?: number;
  className?: string;
  tagClassName?: string;
  tagInputFieldClassName?: string;
  removeClassName?: string;
  tagRegex?: RegExp;
}) => {
  const [internalValue, setInternalValue] = useState<Tag[]>(initialValue);
  const [inputValue, setInputValue] = useState<string>('');
  const lastInputRef = useRef('');

  const tags = controlledValue ?? internalValue;
  const isAtMaxTags = Boolean(maxTags && tags.length >= maxTags);

  const handleChange = (next: Tag[]) => {
    if (!controlledValue) setInternalValue(next);
    onValueChange?.(next);
  };

  const handleDelete = (i: number) => handleChange(tags.filter((_, index) => index !== i));

  const handleAddition = (tag: Tag) => {
    const nextValue = tag.id.trim();
    const isValid = tagRegex.test(nextValue);
    const belowMax = !maxTags || tags.length < maxTags;

    if (isValid && belowMax) {
      handleChange([...tags, tag]);
      setInputValue('');
    } else {
      onInvalidValue?.(nextValue);
      setInputValue(lastInputRef.current);
    }
  };

  const inputProps = {
    autoFocus: false,
    onKeyDownCapture: (event: React.KeyboardEvent<HTMLInputElement>) => {
      const isSeparator = event.key === 'Enter' || event.key === ',';
      const isRemovingKey = event.key === 'Backspace' || event.key === 'Delete';

      if (isRemovingKey) return;

      if (isAtMaxTags) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!isSeparator) return;

      const nextValue = inputValue.trim();
      if (!nextValue) return;

      const isValid = tagRegex.test(nextValue);
      const belowMax = !maxTags || tags.length < maxTags;

      if (!isValid || !belowMax) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
  } as Partial<React.InputHTMLAttributes<HTMLInputElement>> & Record<string, string>;

  return (
    <div>
      <ReactTags
        tags={tags}
        handleDelete={handleDelete}
        handleAddition={handleAddition}
        handleInputChange={(value) => {
          lastInputRef.current = value;
          setInputValue(value);
        }}
        delimiters={delimiters}
        autofocus={false}
        inputProps={inputProps}
        placeholder={placeholder}
        inputValue={inputValue}
        maxLength={20}
        classNames={{
          tags: cn(
            'flex min-h-9 w-full flex-wrap items-center justify-start gap-2 rounded-sm border border-input bg-transparent px-3.25 py-2 text-base font-medium shadow-xs transition-colors placeholder:text-muted-foreground placeholder:font-medium placeholder:text-control focus-within:border-ring focus-within:ring-1/2 focus-within:ring-ring disabled:cursor-not-allowed disabled:bg-muted md:text-sm',
            className,
          ),
          tagInput: 'flex w-full',
          tagInputField: cn(
            'outline-none text-sm text-foreground-heading placeholder-gray-400 bg-transparent min-w-[20px] w-full placeholder:text-muted-foreground placeholder:font-medium placeholder:text-control',
            tagInputFieldClassName,
          ),
          selected: 'contents',
          tag: cn(
            'grid grid-cols-[1fr_12px] items-center gap-1 px-2.5 py-0.5 border w-fit border-border-strong rounded-md bg-card text-sm text-foreground-heading truncate min-w-0  [&>span]:truncate [&>span]:min-w-0 relative [&>button]:absolute [&>button]:pl-1 [&>button]:absolute [&>button]:right-0 [&>button]:h-full [&>button]:pr-2.5 [&>button]:bg-card ',
            tagClassName,
          ),
          remove: cn(
            'flex-shrink-0 cursor-pointer ml-0.5 text-sm text-foreground-caption hover:text-foreground-heading [&_svg]:fill-current',
            removeClassName,
          ),
        }}
      />
    </div>
  );
};
